import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';

import { cn } from '../../lib/cn';

/**
 * docs/02-DESIGN-SYSTEM.md §4.1 — the filter Pill.
 *
 * "Toggle button. Default: white with a `--border-strong` outline. Active:
 * solid navy, white text. Used in Personnel, Documents, Programs, Vocational
 * QA."
 *
 * It is a real `<button>` with `aria-pressed`, not a styled `<div>`: a filter
 * that a screen reader cannot report the state of is a filter that cannot be
 * used. The `FilterBar` that groups these and syncs them to the URL query
 * arrives with the pages in Phase 3.
 */
export interface PillProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'aria-pressed'> {
  active?: boolean;
  /** Optional trailing count, e.g. the number of matching documents. */
  count?: number;
  children?: ReactNode;
}

export const Pill = forwardRef<HTMLButtonElement, PillProps>(function Pill(
  { active = false, count, className, children, type, ...props },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type ?? 'button'}
      aria-pressed={active}
      className={cn(
        'inline-flex h-11 items-center gap-2 rounded-full px-5 text-body-sm font-semibold',
        'transition-colors duration-fast ease-out',
        'disabled:pointer-events-none disabled:opacity-50',
        active
          ? 'bg-navy-600 text-white hover:bg-navy-700'
          : 'border border-strong bg-surface text-primary hover:border-navy-600 hover:text-navy-600',
        className,
      )}
      {...props}
    >
      {children}
      {count !== undefined ? (
        // Decorative: the count duplicates information already in the list, so
        // it is not announced twice.
        <span aria-hidden="true" className={cn('text-caption', !active && 'text-secondary')}>
          {count}
        </span>
      ) : null}
    </button>
  );
});
